import { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";

// ── Camera capture ───────────────────────────────────────────────────────────
// Un `<input type="file" capture="environment">` NO abre la cámara: `capture`
// es una *sugerencia* que solo respetan los navegadores móviles. En escritorio,
// en tablets con Chrome/Edge y en varias webviews se ignora por completo y cae
// al selector de archivos — justo lo que el usuario no quiere. Para abrir la
// cámara de verdad hay que pedir el stream con getUserMedia y tomar el cuadro
// nosotros mismos.

type CameraFailure =
  | "insecure"
  | "unsupported"
  | "permission"
  | "notfound"
  | "busy"
  | "unknown";

const FAILURE_COPY: Record<CameraFailure, { title: string; html: string }> = {
  insecure: {
    title: "La cámara necesita una conexión segura",
    html: "El navegador solo permite abrir la cámara en sitios <b>https</b>. Abre la plataforma con <code>https://</code>. La fotografía sólo se puede tomar con la cámara.",
  },
  unsupported: {
    title: "Este navegador no puede abrir la cámara",
    html: "El navegador no expone acceso a la cámara. Prueba con Chrome, Edge o Safari actualizados.",
  },
  permission: {
    title: "Permiso de cámara bloqueado",
    html: [
      "Para tomar la fotografía tienes que habilitar el permiso de cámara para este sitio:",
      '<ol style="text-align:left;margin:12px auto 0;max-width:22rem;line-height:1.6">',
      "<li>Toca el <b>candado</b> 🔒 (o el ícono a la izquierda de la dirección) en la barra del navegador.</li>",
      "<li>Entra a <b>Permisos</b> / <b>Configuración del sitio</b>.</li>",
      "<li>Cambia <b>Cámara</b> a <b>Permitir</b>.</li>",
      "<li>Recarga la página y vuelve a intentar.</li>",
      "</ol>",
      '<p style="margin-top:12px">La fotografía sólo se puede tomar con la cámara: no se aceptan imágenes de la galería.</p>',
    ].join(""),
  },
  notfound: {
    title: "No se encontró ninguna cámara",
    html: "Este dispositivo no tiene una cámara disponible. Usa un dispositivo con cámara para tomar la fotografía.",
  },
  busy: {
    title: "La cámara está ocupada",
    html: "Otra aplicación está usando la cámara. Ciérrala y vuelve a intentar.",
  },
  unknown: {
    title: "No se pudo abrir la cámara",
    html: "Ocurrió un error al iniciar la cámara. Vuelve a intentar.",
  },
};

function classifyError(err: unknown): CameraFailure {
  const name = (err as { name?: string } | null)?.name || "";
  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  )
    return "permission";
  if (
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "OverconstrainedError"
  )
    return "notfound";
  if (name === "NotReadableError" || name === "TrackStartError") return "busy";
  return "unknown";
}

export default function CameraCaptureModal({
  slotLabel,
  isDarkMode,
  onCapture,
  onClose,
}: {
  /** Nombre de la fotografía que se está tomando; se muestra en el visor. */
  slotLabel: string;
  isDarkMode: boolean;
  onCapture: (file: File) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [starting, setStarting] = useState(true);
  const [facingMode, setFacingMode] = useState<"environment" | "user">(
    "environment",
  );
  const [canSwitch, setCanSwitch] = useState(false);
  const [preview, setPreview] = useState<{ url: string; file: File } | null>(
    null,
  );

  // El efecto que abre la cámara no puede depender de props que cambian de
  // identidad en cada render del padre (`onClose` es una arrow inline): se
  // re-ejecutaría y volvería a pedir el stream una y otra vez. Se leen por ref.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isDarkModeRef = useRef(isDarkMode);
  isDarkModeRef.current = isDarkMode;

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const failAndClose = useCallback(
    (failure: CameraFailure) => {
      stopStream();
      onCloseRef.current();
      const copy = FAILURE_COPY[failure];
      const dark = isDarkModeRef.current;
      Swal.fire({
        icon: failure === "permission" ? "warning" : "error",
        title: copy.title,
        html: copy.html,
        confirmButtonText: "Entendido",
        confirmButtonColor: brand.primary,
        background: dark ? "#1f2937" : "#fff",
        color: dark ? "#fff" : "#1f2937",
      });
    },
    [stopStream],
  );

  // Abre el stream; se re-ejecuta al alternar cámara frontal/trasera.
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (typeof window !== "undefined" && window.isSecureContext === false) {
        failAndClose("insecure");
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        failAndClose("unsupported");
        return;
      }
      setStarting(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stopStream();
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // El play() puede rechazarse en Safari; el <video> lleva autoPlay,
          // así que el rechazo no rompe la vista previa.
          videoRef.current.play().catch(() => {});
        }
        // El botón de alternar solo tiene sentido con más de una cámara.
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled)
            setCanSwitch(
              devices.filter((d) => d.kind === "videoinput").length > 1,
            );
        } catch {
          /* enumerateDevices es opcional: si falla, no ofrecemos el cambio */
        }
        if (!cancelled) setStarting(false);
      } catch (err) {
        if (!cancelled) failAndClose(classifyError(err));
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [facingMode, failAndClose, stopStream]);

  // Suelta la cámara al desmontar — sin esto el indicador sigue encendido — y
  // libera el object URL de la vista previa si quedó una sin confirmar.
  const previewUrlRef = useRef<string | null>(null);
  previewUrlRef.current = preview?.url ?? null;
  useEffect(
    () => () => {
      stopStream();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [stopStream],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleShoot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `foto-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setPreview({ url: URL.createObjectURL(file), file });
      },
      "image/jpeg",
      0.9,
    );
  };

  const discardPreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const confirmPreview = () => {
    if (!preview) return;
    const { file, url } = preview;
    stopStream();
    URL.revokeObjectURL(url);
    setPreview(null);
    onCapture(file);
  };

  const handleClose = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    stopStream();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 text-white shrink-0">
        <p className="text-sm font-semibold truncate">{slotLabel}</p>
        <div className="flex items-center gap-1">
          {canSwitch && !preview && (
            <button
              type="button"
              onClick={() =>
                setFacingMode((m) =>
                  m === "environment" ? "user" : "environment",
                )
              }
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              title="Cambiar de cámara"
            >
              <i className="ri-camera-switch-line"></i>
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            title="Cerrar"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      </div>

      <div className="flex-1 relative min-h-0">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-contain ${preview ? "invisible" : ""}`}
        />
        {preview && (
          <img
            src={preview.url}
            alt="Vista previa"
            className="absolute inset-0 w-full h-full object-contain"
          />
        )}
        {starting && !preview && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 gap-2 text-sm">
            <i className="ri-loader-4-line animate-spin"></i>
            Abriendo cámara...
          </div>
        )}
      </div>

      <div className="shrink-0 px-4 py-5 flex items-center justify-center gap-6">
        {preview ? (
          <>
            <button
              type="button"
              onClick={discardPreview}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 hover:bg-white/20 text-white flex items-center gap-1"
            >
              <i className="ri-refresh-line"></i>
              Repetir
            </button>
            <button
              type="button"
              onClick={confirmPreview}
              className="px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              <i className="ri-check-line"></i>
              Usar foto
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleShoot}
            disabled={starting}
            className="w-16 h-16 rounded-full border-4 border-white/70 bg-white/90 hover:bg-white disabled:opacity-40 flex items-center justify-center"
            title="Tomar fotografía"
          >
            <i className="ri-camera-line text-2xl text-gray-900"></i>
          </button>
        )}
      </div>
    </div>
  );
}
